import { useWebSocketContext } from 'contexts/WebSocketContext'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useStore } from 'react-redux'
import {
  setAwaitingResponse,
  setCurrentStreamingMessage,
  setIsLoading,
} from 'slices/aiConversations'
import type { SegmentSaveData } from 'slices/aiConversations/segmentVisualizationTypes'
import type { Message } from 'slices/aiConversations/types'
import { webSocketContextAdapter } from 'utils/webSocketContextAdapter'

import { useAIChatActions } from 'hooks/websocket/useAIChatActions'
import { useAIChatConnection } from 'hooks/websocket/useAIChatConnection'
import { useAIChatMessages } from 'hooks/websocket/useAIChatMessages'

export interface UseAIWebSocketReturn {
  isConnected: boolean
  connectionError: string | null
  isLoading: boolean
  messages: Message[]
  currentStreamingMessage: Message | null
  awaitingResponse: boolean
  sendMessage: (content: string) => void
  saveSegment: (segmentData: SegmentSaveData) => void
  setIsLoading: (loading: boolean) => void
  setAwaitingResponse: (awaiting: boolean) => void
  addMessage: (message: Message) => void
  setMessages: (messages: Message[]) => void
  resetChatId: () => void
  setMessageDroppedCallback: (callback: (() => void) | null) => void
}

/**
 * High-level convenience hook that composes all WebSocket functionality.
 */
export const useAIChat = (): UseAIWebSocketReturn => {
  const dispatch = useDispatch()
  const store = useStore()
  const webSocketContext = useWebSocketContext()

  // Set up context adapter for Redux thunks
  useEffect(() => {
    webSocketContextAdapter.setContext(webSocketContext)
  }, [webSocketContext])

  const {
    messages,
    currentStreamingMessage,
    isLoading,
    awaitingResponse,
    sequenceCounter,
    removeToolCallMessages,
    addMessageWithOrdering,
    setMessagesWithSync,
  } = useAIChatMessages()

  // Create callback wrappers for Redux dispatch compatibility
  const setCurrentStreamingMessageCallback = useCallback(
    (value: React.SetStateAction<Message | null>) => {
      if (typeof value === 'function') {
        // Access current value inside callback to avoid dependency issues
        const currentState = store.getState()
        const currentValue =
          currentState.aiConversations.webSocket.currentStreamingMessage
        const newValue = value(currentValue)
        dispatch(setCurrentStreamingMessage(newValue))
      } else {
        dispatch(setCurrentStreamingMessage(value))
      }
    },
    [dispatch, store]
  )

  const setIsLoadingCallback = useCallback(
    (value: React.SetStateAction<boolean>) => {
      if (typeof value === 'function') {
        const currentState = store.getState()
        const currentValue = currentState.aiConversations.webSocket.isLoading
        const newValue = value(currentValue)
        dispatch(setIsLoading(newValue))
      } else {
        dispatch(setIsLoading(value))
      }
    },
    [dispatch, store]
  )

  const setAwaitingResponseCallback = useCallback(
    (value: React.SetStateAction<boolean>) => {
      if (typeof value === 'function') {
        const currentState = store.getState()
        const currentValue =
          currentState.aiConversations.webSocket.awaitingResponse
        dispatch(setAwaitingResponse(value(currentValue)))
      } else {
        dispatch(setAwaitingResponse(value))
      }
    },
    [dispatch, store]
  )

  const callbacks = useMemo(
    () => ({
      setCurrentStreamingMessage: setCurrentStreamingMessageCallback,
      setIsLoading: setIsLoadingCallback,
      setAwaitingResponse: setAwaitingResponseCallback,
      addMessageWithOrdering,
      removeToolCallMessages,
      sequenceCounter,
    }),
    [
      setCurrentStreamingMessageCallback,
      setIsLoadingCallback,
      setAwaitingResponseCallback,
      addMessageWithOrdering,
      removeToolCallMessages,
      sequenceCounter,
    ]
  )

  const {
    isConnected,
    connectionError,
    wsManager,
    resetChatId,
    setMessageDroppedCallback,
  } = useAIChatConnection(callbacks)

  const {
    sendMessage,
    saveSegment,
    setIsLoading: stableSetIsLoading,
    setAwaitingResponse: stableSetAwaitingResponse,
    addMessage,
    setMessages,
  } = useAIChatActions({
    wsManager,
    addMessageWithOrdering,
    setMessagesWithSync,
    resetChatId,
  })

  return {
    isConnected,
    connectionError,
    isLoading,
    messages,
    currentStreamingMessage,
    awaitingResponse,
    sendMessage,
    saveSegment,
    setIsLoading: stableSetIsLoading,
    setAwaitingResponse: stableSetAwaitingResponse,
    addMessage,
    setMessages,
    resetChatId,
    setMessageDroppedCallback,
  }
}
